import { createContext, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { FormData, Template } from "@/types/document-generator";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/auth/AuthProvider";

interface DocumentGeneratorContextType {
  form: ReturnType<typeof useForm<FormData>>;
  previewContent: string;
  selectedTemplate: Template | null;
  isLoading: boolean;
  handleTemplateSelect: (template: Template) => Promise<void>;
  generateDocument: () => Promise<any>;
  setPreviewContent: (content: string) => void;
}

const DocumentGeneratorContext = createContext<DocumentGeneratorContextType | undefined>(undefined);

export function DocumentGeneratorProvider({ children }: { children: React.ReactNode }) {
  const [previewContent, setPreviewContent] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const form = useForm<FormData>({
    defaultValues: {
      title: "",
      content: "",
      template_id: "",
      case_id: undefined,
    },
  });

  const handleTemplateSelect = async (template: Template) => {
    setSelectedTemplate(template);
    form.setValue("template_id", template.id);
    form.setValue("title", template.title);
    setPreviewContent(template.content);
  };

  const generateDocument = async () => {
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Du må være logget inn for å generere dokumenter",
      });
      return;
    }

    const values = form.getValues();
    
    // Validate title is not empty
    if (!values.title || values.title.trim() === "") {
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Dokumentet må ha en tittel",
      });
      return;
    }

    try {
      setIsLoading(true);
      console.log("Calling generate-document-rag function...");
      
      // First, generate content using RAG AI service
      const { data: generatedData, error: generationError } = await supabase.functions
        .invoke('generate-document-rag', {
          body: {
            title: values.title.trim(),
            content: values.content || previewContent,
            documentType: values.case_id ? 'workflow_generated' : 'generated'
          }
        });

      if (generationError) {
        console.error('Error generating document with RAG:', generationError);
        throw generationError;
      }

      if (!generatedData?.content) {
        throw new Error("No content generated from RAG service");
      }

      // Then, save the generated document
      console.log("Saving generated document to database...");
      const { data: document, error: saveError } = await supabase
        .from('documents')
        .insert({
          title: values.title.trim(),
          content: generatedData.content,
          document_type: values.case_id ? 'workflow_generated' : 'generated',
          user_id: user.id,
          case_id: values.case_id || null,
          template_id: values.template_id || null
        })
        .select()
        .single();

      if (saveError) {
        console.error('Error saving document:', saveError);
        throw saveError;
      }

      // Invalidate and refetch documents query
      await queryClient.invalidateQueries({ queryKey: ['documents', user.id] });
      if (values.case_id) {
        await queryClient.invalidateQueries({ queryKey: ['workflow-document'] });
      }

      toast({
        title: "Dokument opprettet",
        description: "Dokumentet har blitt generert og lagret",
      });

      return document;
    } catch (error: any) {
      console.error('Error in document generation:', error);
      toast({
        variant: "destructive",
        title: "Feil ved dokumentgenerering",
        description: "Kunne ikke generere dokumentet. Vennligst prøv igjen.",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    form,
    previewContent,
    selectedTemplate,
    isLoading,
    handleTemplateSelect,
    generateDocument,
    setPreviewContent,
  };

  return (
    <DocumentGeneratorContext.Provider value={value}>
      {children}
    </DocumentGeneratorContext.Provider>
  );
}

export function useDocumentGenerator() {
  const context = useContext(DocumentGeneratorContext);
  if (context === undefined) {
    throw new Error("useDocumentGenerator must be used within DocumentGeneratorProvider");
  }
  return context;
}